import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Menu,
  Search,
  ScanLine,
  Building2,
  User,
  Ticket,
  ChevronDown,
  Database,
  MapFolded,
  MapPin,
  Package,
} from './icons';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModal } from '../contexts/AuthModalContext';
import { MobileMenu, MobileMenuLink, MenuTile } from './MobileMenu';
import { HeaderSearch } from './HeaderSearch';
import { ThemeToggleSwitch, ThemeToggleSegment } from './ThemeToggle';
import { UserMenu } from './UserMenu';
import { usePopIn } from '../hooks/usePopIn';
import { immersiveConfig } from '../lib/immersive';
import logoUrl from '../assets/logo.webp';

export function Header() {
  const { user, isAuthenticated, logout } = useAuth();
  const { open: openAuthModal } = useAuthModal();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const onSearchPage = location.pathname === '/search';
  // 沉浸式顶栏（移动端：透明浮于 hero 之上 → 下滑变毛玻璃胶囊 + 反白 logo/图标）。
  // 路由配置集中在 lib/immersive.ts（Header 与状态栏配色共用）。range 写入 --zx-hd-range。
  const immersiveCfg = immersiveConfig(location.pathname);
  const immersive = !!immersiveCfg;
  const immersiveRange = immersiveCfg?.range ?? null;

  // 移动端首页顶端沉浸态（logo 反白、图标转白、实体层淡出、渐变遮罩淡出）现由「滚动驱动 CSS」接管
  // （globals.css 的 .zx-home .zx-hd-* + Hero 的 .zx-hero-*）——零 JS scroll 监听、与原生滚动同步、无抖动。
  // 图标按钮带 zx-hd-icon 类参与颜色过渡；首页给 <header> 加 .zx-home 启用这些动画。
  const mobileIconBtnClass =
    'zx-hd-icon flex h-9 w-9 items-center justify-center rounded-full text-fg-muted transition-colors hover:bg-surface-muted hover:text-fg';

  async function handleLogout() {
    setMenuOpen(false);
    await logout();
    navigate('/');
  }

  function closeMenu() {
    setMenuOpen(false);
  }

  return (
    <header
      className={`sticky top-0 z-30${immersive ? ' zx-home' : ''}`}
      style={immersiveRange ? ({ '--zx-hd-range': immersiveRange } as CSSProperties) : undefined}
    >
      {/* 沉浸式渐变遮罩：移动端沉浸页(首页/数据来源)顶端，上半透明→下完全透明，随下滑由滚动驱动动画淡出(.zx-hd-scrim)。
          叠在 hero 之上(hero 用负边距塞到 header 之下)，让首屏 header 融入大图；降级时 opacity:0 隐藏。 */}
      {immersive && (
        <div
          aria-hidden
          className="zx-hd-scrim pointer-events-none absolute inset-0 lg:hidden"
          style={{
            background:
              'linear-gradient(to bottom, rgba(4,12,32,0.52) 0%, rgba(4,12,32,0.18) 52%, rgba(4,12,32,0) 100%)',
          }}
        />
      )}
      {/* ============ 桌面端：三颗悬浮玻璃胶囊（>= lg） ============ */}
      <div className="relative mx-auto hidden w-full max-w-[1600px] items-center gap-3 px-4 pb-2 pt-3 lg:flex">
        {/* 胶囊①：logo + 导航 */}
        <div className="liquid-glass liquid-glass-float flex h-12 shrink-0 items-center gap-0.5 rounded-full border border-line pl-3 pr-2">
          <Link to="/" className="flex items-center pr-1.5">
            <img src={logoUrl} alt="知行数据分析" className="header-logo h-8 w-auto" />
          </Link>
          <NavLink to="/" end className={({ isActive }) => navBlockClass(isActive)}>
            首页
          </NavLink>
          <DatasetsNavMenu />
          {/* 完整复现包单开一项而不是塞进「数据分类」下拉：它是增值服务、不是数据浏览入口，
              放在下拉里既不好找、语义也串（见 pages/ReproductionPackage.tsx）。 */}
          <NavLink
            to="/reproduction-package"
            className={({ isActive }) =>
              `${navBlockClass(isActive)} inline-flex items-center gap-1.5`
            }
          >
            <Package className="h-4 w-4 flex-shrink-0" aria-hidden />
            完整复现包
          </NavLink>
          {/* 兑换码是站内唯一的履约入口（淘宝下单 → 客服发码 → 这里兑换），
              2026-09-23 从普通导航项提为红色实心胶囊 + 票券图标：混在灰字导航里会被整条掠过。
              红底白字是刻意的强调色块，不参与 token 翻色。 */}
          <NavLink
            to="/redeem"
            className={({ isActive }) =>
              `ml-1.5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-danger-fill px-3.5 py-2 text-[13px] font-semibold text-fg-on-accent shadow-[0_6px_16px_-7px_rgba(190,36,44,0.85)] transition-[transform,filter] duration-150 hover:-translate-y-px hover:brightness-110${
                isActive ? ' ring-1 ring-inset ring-white/55' : ''
              }`
            }
          >
            <Ticket className="h-4 w-4 flex-shrink-0" aria-hidden />
            兑换码
          </NavLink>
        </div>

        {/* 弹性空白：把搜索 + 账户推到最右 */}
        <div className="flex-1" />

        {/* 胶囊②：搜索 */}
        {!onSearchPage && (
          <div className="liquid-glass liquid-glass-float flex h-12 w-[300px] min-w-[160px] items-stretch rounded-full border border-line">
            <HeaderSearch variant="desktop" />
          </div>
        )}

        {/* 胶囊③：日夜切换器 + 账户 */}
        <div className="liquid-glass liquid-glass-float flex h-12 shrink-0 items-center gap-2 rounded-full border border-line px-2.5">
          <ThemeToggleSwitch />
          {isAuthenticated && user ? (
            <UserMenu user={user} />
          ) : (
            <>
              <button
                type="button"
                onClick={() => openAuthModal({ mode: 'login' })}
                className="px-1.5 text-[13px] text-fg-muted hover:text-fg"
              >
                登录
              </button>
              <button
                type="button"
                onClick={() => openAuthModal({ mode: 'register' })}
                className="rounded-full bg-navy-fill px-4 py-1.5 text-[13px] text-white hover:bg-navy-fill-strong"
              >
                注册
              </button>
            </>
          )}
        </div>
      </div>

      {/* ============ 移动端（< lg）：单颗悬浮胶囊 ============ */}
      {/* pt 含 env(safe-area-inset-top)：viewport-fit=cover 后把胶囊推到状态栏/刘海之下（非安全区设备 env()=0） */}
      <div className="relative px-3 pt-[calc(0.75rem+env(safe-area-inset-top))] lg:hidden">
        <div className="relative flex h-12 items-center justify-between gap-2 rounded-full pl-3 pr-1.5">
          {/* 胶囊磨砂底：首页随下滑由滚动驱动淡入(.zx-hd-solid)；其余页/降级时常显(实体) */}
          <div
            aria-hidden
            className="zx-hd-solid liquid-glass liquid-glass-float pointer-events-none absolute inset-0 rounded-full border border-line"
          />
          <Link to="/" className="relative z-10 flex items-center">
            <img
              src={logoUrl}
              alt="知行数据分析"
              className="header-logo zx-hd-logo h-8 w-auto"
            />
          </Link>
          <div className="relative z-10 flex items-center gap-0.5">
            {isAuthenticated && (
              <button
                type="button"
                onClick={() => navigate('/scan-login')}
                className={mobileIconBtnClass}
                aria-label="扫码登录电脑"
              >
                <ScanLine className="h-5 w-5" />
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate('/search')}
              className={mobileIconBtnClass}
              aria-label="搜索数据集"
            >
              <Search className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className={mobileIconBtnClass}
              aria-label="打开菜单"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      <MobileMenu open={menuOpen} onClose={closeMenu}>
        {/* 高级搜索：全宽行（带图标） */}
        <MobileMenuLink to="/search" onClick={closeMenu} icon={<Search className="h-4 w-4" />}>
          进入高级搜索
        </MobileMenuLink>

        {/* 数据浏览：3 图标磁贴（已去掉「全部数据集」入口） */}
        <p className="px-1 pb-1 pt-2 text-[11px] font-medium tracking-wide text-fg-subtle">数据浏览</p>
        <div className="grid grid-cols-3 gap-2">
          <MenuTile
            to="/datasets?cat=company&lv=category"
            onClick={closeMenu}
            icon={<Building2 className="h-6 w-6" />}
            label="上市公司"
          />
          <MenuTile
            to="/datasets?cat=province&lv=category"
            onClick={closeMenu}
            icon={<MapFolded className="h-6 w-6" />}
            label="省级数据"
          />
          <MenuTile
            to="/datasets?cat=city&lv=category"
            onClick={closeMenu}
            icon={<MapPin className="h-6 w-6" />}
            label="地级市"
          />
        </div>

        {/* 账户：兑换码在最前；登录后 个人中心/扫码登录，未登录 登录/注册 */}
        <p className="px-1 pb-1 pt-3 text-[11px] font-medium tracking-wide text-fg-subtle">账户</p>
        <div className="grid grid-cols-3 gap-2">
          <MenuTile
            to="/redeem"
            onClick={closeMenu}
            icon={<Ticket className="h-6 w-6" />}
            label="兑换码"
          />
          {isAuthenticated && user ? (
            <>
              <MenuTile
                to="/profile"
                onClick={closeMenu}
                icon={<User className="h-6 w-6" />}
                label="个人中心"
              />
              <MenuTile
                to="/scan-login"
                onClick={closeMenu}
                icon={<ScanLine className="h-6 w-6" />}
                label="扫码登录电脑"
              />
            </>
          ) : (
            <>
              <MenuTile
                onClick={() => {
                  closeMenu();
                  openAuthModal({ mode: 'login' });
                }}
                icon={<User className="h-6 w-6" />}
                label="登录"
              />
              <MenuTile
                onClick={() => {
                  closeMenu();
                  openAuthModal({ mode: 'register' });
                }}
                icon={<User className="h-6 w-6" />}
                label="注册"
                tone="solid"
              />
            </>
          )}
        </div>

        {isAuthenticated && user && (
          <button
            onClick={handleLogout}
            className="mt-2 block w-full rounded-control border border-danger-fill bg-danger-tint px-3 py-2 text-center text-sm font-medium text-danger transition-colors hover:bg-danger-fill hover:text-white"
          >
            退出登录
          </button>
        )}

        <div className="mx-1 my-2 border-t border-line-subtle" />
        <ThemeToggleSegment />
      </MobileMenu>
    </header>
  );
}

function navBlockClass(isActive: boolean) {
  return `relative whitespace-nowrap rounded-full px-3 py-2 text-[13px] transition-colors ${
    isActive
      ? 'bg-navy-fill font-medium text-white'
      : 'text-fg-muted hover:bg-surface-muted hover:text-fg'
  }`;
}

/**
 * 桌面端「数据分类」导航（2026-09-23）。
 *
 * 原本「全部数据集 / 上市公司数据 / 省级数据 / 地级市数据」四项平铺在 logo 胶囊里，
 * 占掉导航区大半宽度。现收成一个触发项，悬停展开浮层选择。
 *
 * 触发项本身可点（→ /datasets 全部数据集）：做成纯悬停的死项会让「一键到全部数据集」
 * 这条最常用路径消失，且鼠标点上去没反应是可供性硬伤。
 *
 * 悬停离开留 140ms 宽限并用 pt-2 把触发项与浮层之间的空隙纳入 hover 区，
 * 否则鼠标从触发项移向浮层的途中会穿过「无人区」导致浮层闪退。
 * 键盘可达：聚焦即展开，Esc 收起。移动端不用本组件（走 MobileMenu 抽屉）。
 */
const DATASET_NAV_ITEMS = [
  { code: null, to: '/datasets', label: '全部数据集', hint: '按分类浏览全部已上架' },
  { code: 'company', to: '/datasets?cat=company&lv=category', label: '上市公司数据', hint: 'A 股上市公司面板' },
  { code: 'province', to: '/datasets?cat=province&lv=category', label: '省级数据', hint: '分省宏观指标' },
  { code: 'city', to: '/datasets?cat=city&lv=category', label: '地级市数据', hint: '地级市面板指标' },
] as const;

function datasetNavIcon(code: string | null, className: string) {
  if (code === 'company') return <Building2 className={className} />;
  if (code === 'province') return <MapFolded className={className} />;
  if (code === 'city') return <MapPin className={className} />;
  return <Database className={className} />;
}

function DatasetsNavMenu() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | null>(null);

  usePopIn(panelRef, open, { origin: 'top left', y: 6, scale: 0.97, duration: 0.18 });

  const isOnDatasets = location.pathname === '/datasets';
  const currentCat = new URLSearchParams(location.search).get('cat');

  const cancelClose = () => {
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), 140);
  };

  // 卸载时清掉悬挂的定时器，避免在已卸载组件上 setState
  useEffect(() => cancelClose, []);

  // 路由一变就收起：点浮层里的链接后浮层要立刻消失（鼠标此时仍停在原处，不会触发 mouseleave）
  useEffect(() => {
    setOpen(false);
    cancelClose();
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  return (
    <div
      ref={rootRef}
      className="relative"
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
      onFocus={() => setOpen(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <Link
        to="/datasets"
        aria-haspopup="menu"
        aria-expanded={open}
        className={`${navBlockClass(isOnDatasets)} inline-flex items-center gap-1`}
      >
        数据分类
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </Link>

      {open && (
        // pt-2 而非 mt-2：外层包裹参与 hover 命中，把触发项与卡片间的 8px 空隙一并纳入悬停区
        <div className="absolute left-0 top-full z-40 pt-2">
          <div
            ref={panelRef}
            role="menu"
            className="w-64 overflow-hidden border border-line bg-surface shadow-sm"
          >
            {DATASET_NAV_ITEMS.map((item, i) => {
              const isActive = isOnDatasets && currentCat === item.code;
              return (
                <div key={item.label}>
                  {/* 「全部数据集」与三个分类之间分隔：前者是入口，后三者是并列选项 */}
                  {i === 1 && <div className="border-t border-line" />}
                  <Link
                    to={item.to}
                    role="menuitem"
                    className={`flex items-start gap-2.5 px-3 py-2.5 transition-colors ${
                      isActive
                        ? 'bg-navy-surface text-navy'
                        : 'text-fg-muted hover:bg-surface-muted hover:text-fg'
                    }`}
                  >
                    {datasetNavIcon(item.code, 'mt-0.5 h-4 w-4 flex-shrink-0')}
                    <span className="min-w-0 leading-tight">
                      <span className="block text-[13px] font-medium">{item.label}</span>
                      <span className="mt-0.5 block text-[11.5px] text-fg-subtle">{item.hint}</span>
                    </span>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


